<template>
  <div class="container mt-5">
    <div class="card p-4 shadow border-0" style="border-radius: 16px;">
      <h4 class="mb-4" style="color: #b03a45;">{{ $t("employeesAdd.title") }}</h4>

      <form @submit.prevent="handleSubmit">
        <div class="row">
          <!-- Firstname -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t("employeesAdd.firstname") }}</label>
              <input v-model="form.firstname" type="text" class="form-control" required />
            </div>
          </div>

          <!-- Lastname -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t("employeesAdd.lastname") }}</label>
              <input v-model="form.lastname" type="text" class="form-control" required />
            </div>
          </div>

          <!-- Email -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t("employeesAdd.email") }}</label>
              <input v-model="form.email" type="email" class="form-control" required />
            </div>
          </div>

          <!-- Password -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t("employeesAdd.password") }}</label>
              <div class="position-relative">
                <input 
                  v-model="form.password" 
                  :type="showPassword ? 'text' : 'password'" 
                  class="form-control" 
                  required 
                />
                <button
                  type="button"
                  class="btn position-absolute end-0 top-50 translate-middle-y me-2"
                  style="background: none; border: none; color: #6c757d; padding: 0; z-index: 10;"
                  @click="showPassword = !showPassword"
                >
                  <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t("employeesAdd.confirmPassword") }}</label>
              <div class="position-relative">
                <input 
                  v-model="form.password_confirmation" 
                  :type="showConfirmPassword ? 'text' : 'password'" 
                  class="form-control" 
                  required 
                />
                <button
                  type="button"
                  class="btn position-absolute end-0 top-50 translate-middle-y me-2"
                  style="background: none; border: none; color: #6c757d; padding: 0; z-index: 10;"
                  @click="showConfirmPassword = !showConfirmPassword"
                >
                  <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Rôle : on envoie le NOM (role) attendu par le backend -->
          <div class="col-md-6 mb-4">
            <div class="p-3 bg-white shadow-sm rounded">
              <label class="form-label">{{ $t("employeesAdd.role") }}</label>
              <select v-model="form.role" class="form-select" required>
                <option disabled value="">{{ $t("employeesAdd.rolePlaceholder") }}</option>
                <option
                  v-for="role in roles"
                  :key="role.id"
                  :value="role.name"
                >
                  {{ role.name }}
                </option>
              </select>

              <small v-if="!loadingRoles && roles.length === 0" class="text-muted">
                {{ $t("employeesAdd.noRolesFound") }}
              </small>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end">
          <button type="submit" class="btn text-white" style="background-color: #b03a45;">
            {{ $t("employeesAdd.next") }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import axios from '@/api'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()

// Le backend attend : firstname, lastname, email, password, password_confirmation, role (NOM)
const form = ref({
  firstname: '',
  lastname: '',
  email: '',
  password: '',
  password_confirmation: '',
  role: '' // <-- NOM du rôle (ex: "employee")
})

const showPassword = ref(false)
const showConfirmPassword = ref(false)

const roles = ref([])
const loadingRoles = ref(true)

const fetchRoles = async () => {
  try {
    // Endpoint Laravel qui retourne les rôles (ex: Role::all())
    const res = await axios.get('/roles')
    roles.value = res.data.data || res.data
  } catch (error) {
    console.error('Erreur chargement des rôles :', error)
    toast.error(t('employeesAdd.roleLoadError'))
  } finally {
    loadingRoles.value = false
  }
}

const handleSubmit = async () => {
  if (!form.value.role) {
    toast.error(t('employeesAdd.roleError'))
    return
  }

  if (form.value.password !== form.value.password_confirmation) {
    toast.error(t('employeesAdd.passwordMismatch'))
    return
  }

  try {
    const { data } = await axios.post('/register', form.value)
    toast.success(t('employeesAdd.successMessage'))
    router.push({ path: '/employee/information', query: { user_id: data.user.id } })
  } catch (error) {
    console.error(error)
    if (error.response?.status === 422) {
      const errors = error.response.data.errors
      const firstError = Object.values(errors)?.[0]?.[0] || t('employeesAdd.validationError')
      toast.error(firstError)
    } else {
      toast.error(t('employeesAdd.serverError'))
    }
  }
}

onMounted(() => {
  fetchRoles()
})
</script>
